import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { scaleBand, scaleLinear, max, axisBottom, axisLeft, axisRight, select, format, pointer } from "d3";

export default function ThermalEnergyConsumed({ className, data }: any) {
  const svgRef = useRef(null);
  const [width, setWidth] = useState(1000);
  const [height, setHeight] = useState(200);
  const [tooltipValue1, setTooltipValue1] = useState("");
  const [tooltipValue2, setTooltipValue2] = useState("");

  const margin = useMemo(() => ({ left: 1, right: 50, top: 2, bottom: 10 }), []);

  const minorTickSize = 10;
  const allowance = 54;
  const bottom = height - margin.bottom - margin.top - minorTickSize - 14;

  const xScale = useMemo(
    () =>
      scaleBand()
        .domain(
          data.map((d: any) =>
            new Date(d.timestamp).toLocaleString("en-GB", {
              month: "short",
              day: "2-digit",
            })
          )
        )
        .padding(0.5)
        .range([minorTickSize + margin.left + allowance, width - margin.right]),
    [data, width, margin]
  );

  const xSubgroup = scaleBand().domain(["heat_consumed_by_farm", "destroyed_energie_mw"]).range([0, xScale.bandwidth()]).padding(0.05);

  const yScale = useMemo(() => {
    const maxValue = max(
      data ? [...data.map((d: any) => parseFloat(d.consumed_energie_mw)), ...data.map((d: any) => parseFloat(d.destroyed_energie_mw))] : []
    );

    return scaleLinear()
      .domain([0, maxValue + (maxValue > 2 * 10**6 ? maxValue / 3.5 : 15**5)])
      .nice()
      .range([bottom, margin.top]);
  }, [data, bottom, margin]);

  const drawAxis = useCallback(
    ({ container, xScale, yScale, right, tickArguments, tickSize, tickFormat, color, transform, noText }: any) => {
      const scale = xScale || yScale;
      const axisType = xScale ? axisBottom : !right ? axisLeft : axisRight;
      const axis = axisType(scale).tickArguments(tickArguments).tickSize(tickSize).tickFormat(tickFormat);
      const axisGroup = container.append("g").attr("class", "axis").attr("color", color).attr("transform", transform).call(axis);
      axisGroup.selectAll("line").attr("stroke", color).attr("stroke-width", "0.5");
      axisGroup
        .selectAll("text")
        .attr("color", "#95A0B7")
        // .attr("transform", "rotate(-45)")
        // .attr("transform-origin", "0 20")
        .attr("font-size", "0.75rem");

      if (xScale && data.length > 14) {
        axisGroup.selectAll("text").attr("transform", "rotate(-45)").attr("style", "transform-origin:15px 15px");
      }
      if (noText) {
        axisGroup.selectAll("text").remove();
      }
    },
    [data]
  );

  useEffect(() => {
    // Create root container where we will append all other chart elements
    const svgEl = select(svgRef.current).select(".axis-group");
    svgEl.selectAll(".axis").remove();
    // Add X grid lines with labels
    drawAxis({
      xScale,
      container: svgEl,
      color: "rgb(149 160 183)",
      tickSize: 0,
      tickArguments: [1, "%b %d"],
      transform: `translate(0, ${height - margin.bottom + margin.top - minorTickSize - 14})`,
    });

    // Add Y grid lines with labels
    drawAxis({
      yScale,
      container: svgEl,
      color: "rgb(149 160 183)",
      tickSize: 0,
      tickArguments: [1, "~s"],
      transform: `translate(${margin.left + minorTickSize + allowance}, ${margin.top})`,
      tickFormat: (val: any) => `${format("~s")(val)}W`,
    });
    // Add Y grid lines without labels
    drawAxis({
      yScale,
      container: svgEl,
      noText: true,
      color: "rgba(149, 160, 183, 0.2)",
      tickSize: margin.right - width + allowance + minorTickSize,
      tickArguments: [5, "~s"],
      transform: `translate(${margin.left + minorTickSize + allowance}, ${margin.top})`,
      // tickFormat: (val: any) => `${val}%`,
    });

    svgEl.selectAll(".domain").remove();
  }, [drawAxis, width, height, margin, xScale, yScale]);

  const resizeWindow = () => {
    const rect = svgRef.current.getBoundingClientRect();
    setWidth(rect.width);
    setHeight(rect.height);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener("resize", resizeWindow);
    return () => window.removeEventListener("resize", resizeWindow);
  }, []);

  const drawBackground = () => {
    // reset background size to defaults
    const contentBackground = select(svgRef.current).select(".contentBackground");
    contentBackground.attr("width", 120).attr("height", 40);

    // calculate new background size
    const tooltipContentElement = select(svgRef.current).select(".content").node() as SVGElement;
    if (!tooltipContentElement) return;

    const contentSize = tooltipContentElement.getBoundingClientRect();

    contentBackground.attr("width", contentSize.width + 12).attr("height", contentSize.height + 4);

    return contentSize;
  };

  const handleMouseOver = () => {
    select(svgRef.current).select(".tooltip").transition().duration(10).attr("display", "block");
  };

  const handleMouseMove = (e: any, index: number) => {
    const [, y] = pointer(e);
    const x = xScale(
      new Date(data[index].timestamp).toLocaleString("en-GB", {
        month: "short",
        day: "2-digit",
      })
    );
    const tooltip = select(svgRef.current).select(".tooltip");
    setTooltipValue1(`${format("~s")(data[index].consumed_energie_mw)}W`);
    setTooltipValue2(`${format("~s")(data[index].destroyed_energie_mw)}W`);

    tooltip.transition().duration(10).attr("display", "block");
    const contentSize = drawBackground();

    const translateX = contentSize.width + x + 50 > width ? x - 50 - contentSize.width : x + 50;
    const translateY = contentSize.height + y + 10 > height ? y + 10 - contentSize.height : y + 10;

    tooltip.attr("transform", `translate(${translateX}, ${translateY})`);
  };

  const handleMouseOut = (e: any) => {
    select(svgRef.current).select(".tooltip").transition().duration(10).attr("display", "none");
  };

  return (
    <div className={className}>
      <div className="font-bold my-3">Thermal Energy Consumed Vs Destroyed</div>
      <svg overflow="visible" className="cursor-crosshair" ref={(node) => (node ? (svgRef.current = node) : null)} viewBox={`0 0 ${width} ${height}`}>
        <g className="axis-group" />
        <g className="chart">
          {data.map((item: any, index: number) => (
            <g
              key={item.timestamp}
              transform={`translate(${xScale(
                new Date(item.timestamp).toLocaleString("en-GB", {
                  month: "short",
                  day: "2-digit",
                })
              )},${0})`}
            >
              <rect
                fill="#2AC0D5"
                x={xSubgroup("heat_consumed_by_farm")}
                y={yScale(item.consumed_energie_mw) + margin.top}
                width={xSubgroup.bandwidth()}
                height={bottom - yScale(item.consumed_energie_mw)}
                onMouseOver={handleMouseOver}
                onMouseMove={(e: any) => handleMouseMove(e, index)}
                onMouseOut={handleMouseOut}
              />
              <rect
                fill="#d1d5db"
                x={xSubgroup("destroyed_energie_mw")}
                y={yScale(item.destroyed_energie_mw) + margin.top}
                width={xSubgroup.bandwidth()}
                height={bottom - yScale(item.destroyed_energie_mw)}
                onMouseOver={handleMouseOver}
                onMouseMove={(e: any) => handleMouseMove(e, index)}
                onMouseOut={handleMouseOut}
              />
            </g>
          ))}
        </g>
        <g className="tooltip" display="none">
          <rect
            className="contentBackground"
            rx={1}
            ry={1}
            fill="#fff"
            strokeWidth={1}
            stroke="#f0f0f0"
            filter="drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.05))"
          />

          <g className="content">
            <g transform={`translate(1,10)`}>
              {/* <circle r={6} fill="#2AC0D5" /> */}
              <text className="item-value" fill="#2AC0D5" transform="translate(4,4)" fontSize="11">
                Thermal energy consumed: {tooltipValue1}
              </text>
            </g>
            <g transform={`translate(1,24)`}>
              {/* <circle r={6} fill="#d1d5db" /> */}
              <text className="item-value" fill="#a0a0a0" transform="translate(4,4)" fontSize="11">
                <tspan x="0" dy="0.5em">
                  Thermal energy destroyed: {tooltipValue2}
                </tspan>
              </text>
            </g>
          </g>
        </g>
        {!data.length ? (
          <g>
            <rect
              rx={1}
              ry={1}
              width={width}
              height={height}
              fill="rgba(255, 255, 255, 0.75)"
              strokeWidth={1}
              stroke="#f0f0f0"
              filter="drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.05))"
            />
            <text fill="#5f698f" fontSize={24} transform={`translate(${margin.left + minorTickSize + allowance + 144}, ${margin.top + 72})`}>
              No available data
            </text>
          </g>
        ) : null}
      </svg>
      <div className="flex items-center mt-4 ml-5 text-sm">
        <div className="bg-cyan-600 w-4 h-4 my-1 mr-2"></div>
        <div className="mr-3">Thermal energy consumed</div>
        <div className="bg-gray-300 w-4 h-4 my-1 mr-2"></div>
        <div>Thermal energy destroyed</div>
      </div>
    </div>
  );
}
